import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ref, update } from "firebase/database";
import {
  LuSearch,
  LuX,
  LuChevronDown,
  LuPin,
  LuEllipsisVertical,
  LuPlus,
  LuVolumeX,
  LuShieldBan,
  LuTrash2,
} from "react-icons/lu";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import AppPage, { State } from "../components/AppPage";
import Avatar from "../components/UserAvatar";
import ModalSheet from "../components/ModalSheet";
import useMessageInbox from "../hooks/useMessageInbox";
import { filterInbox, chatIdFor } from "../utils/messageInbox";
import { NICHES } from "../utils/appData";
const tabs = ["All", "DMs", "Groups", "Unread", "Requests", "Blocked"];
export default function Chats() {
  const { currentUser } = useUser();
  const uid = currentUser?.uid;
  const inbox = useMessageInbox();
  const location = useLocation();
  const [tab, setTab] = useState(
    tabs.includes(location.state?.incommingTab)
      ? location.state.incommingTab
      : "All",
  );
  const [search, setSearch] = useState("");
  const [niche, setNiche] = useState("");
  const [more, setMore] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const list = useMemo(
    () => filterInbox(inbox.list, tab, search, niche),
    [inbox.list, tab, search, niche],
  );
  const header = useMemo(
    () => (
      <Link
        to="/create-group"
        aria-label="Create group"
        className="grid h-10 w-10 place-items-center rounded-full bg-[#327691] text-white"
      >
        <LuPlus size={23} />
      </Link>
    ),
    [],
  );
  async function action(type) {
    if (!uid || !more || busy) return;
    setBusy(true);
    setError("");
    try {
      const patch = {};
      const row = more;
      const key = row.type === "group" ? row.id : chatIdFor(uid, row.id);
      if (type === "pin")
        patch[
          `Users/${uid}/${row.type === "group" ? "pinnedGroups" : "pinnedChats"}/${row.id}`
        ] = row.pinned ? null : true;
      if (type === "mute")
        patch[`Users/${uid}/mutedChats/${key}`] = row.muted ? null : true;
      if (type === "block") {
        patch[`Users/${uid}/blockedUsers/${row.id}`] = row.blocked
          ? null
          : true;
        patch[`Users/${uid}/requestChats/${row.id}`] = null;
      }
      if (type === "hide") {
        if (
          !window.confirm(
            "Remove this conversation from your list? A new message brings it back.",
          )
        )
          return;
        patch[`Users/${uid}/hiddenChats/${row.id}`] = Date.now();
      }
      if (Object.keys(patch).length) await update(ref(database), patch);
      setMore(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <AppPage title="Messages" headerActions={header}>
      <div className="mb-3 flex min-w-0 items-center gap-2">
        <label className="flex h-12 min-w-0 flex-1 items-center gap-2 rounded-2xl border border-slate-200 bg-[#F5F9FE] px-3 text-slate-500 dark:border-[#363e46] dark:bg-[#23272c]">
          <LuSearch size={20} className="shrink-0" />
          <input
            className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none dark:text-white"
            aria-label="Search messages"
            placeholder="Search here..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              aria-label="Clear message search"
            >
              <LuX size={18} />
            </button>
          )}
        </label>
        <label className="relative flex h-12 max-w-[140px] shrink-0 items-center rounded-2xl bg-[#327691] pl-3 pr-7 text-xs font-semibold text-white">
          <span className="sr-only">Filter by niche</span>
          <select
            className="max-w-full appearance-none truncate bg-transparent outline-none [&>option]:bg-white [&>option]:text-slate-900"
            value={niche}
            onChange={(e) => setNiche(e.target.value)}
          >
            <option value="">All Niches</option>
            {NICHES.map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
          <LuChevronDown
            className="pointer-events-none absolute right-2"
            size={14}
          />
        </label>
      </div>
      <div
        className="mb-4 flex gap-2 overflow-x-auto pb-1"
        aria-label="Message filters"
      >
        {tabs.map((value) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            aria-pressed={tab === value}
            className={`h-9 shrink-0 rounded-xl px-4 text-[13px] font-semibold ${tab === value ? "bg-[#327691] text-white" : "bg-white text-slate-700 dark:bg-[#23272c] dark:text-slate-200"}`}
          >
            {value}
          </button>
        ))}
      </div>
      <State loading={inbox.loading} error={error || inbox.error} />
      <section
        className="overflow-hidden rounded-[20px] border border-black/5 bg-white shadow-sm dark:border-[#363e46] dark:bg-[#23272c]"
        aria-label="Conversations"
      >
        {list.map((row) => (
          <article
            key={row.type + row.id}
            className="flex min-w-0 items-center gap-2 border-b border-slate-100 px-3 py-3 last:border-0 dark:border-white/5 md:px-4"
          >
            <Link
              className="flex min-w-0 flex-1 items-center gap-3"
              to={
                row.blocked
                  ? "#"
                  : row.type === "group"
                    ? `/group-chat/${row.id}`
                    : `/chat/${row.id}`
              }
              onClick={(e) => {
                if (row.blocked) {
                  e.preventDefault();
                  setMore(row);
                }
              }}
            >
              <Avatar
                user={row}
                className="h-12 w-12 shrink-0 rounded-full object-cover"
              />
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <strong
                    className={`truncate text-[15px] font-bold ${row.role === "Host" ? "text-[#B8860B] dark:text-[#EFBF04]" : ""}`}
                  >
                    {row.name}
                  </strong>
                  {row.pinned && (
                    <LuPin size={12} className="shrink-0 text-slate-400" />
                  )}
                  {row.muted && (
                    <LuVolumeX size={12} className="shrink-0 text-slate-400" />
                  )}
                </span>
                <small
                  className={`mt-1 block truncate text-xs ${row.unread ? "font-semibold text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400"}`}
                >
                  {row.blocked
                    ? "Blocked"
                    : row.typing
                      ? "Typing…"
                      : row.message}
                </small>
              </span>
              <span className="flex shrink-0 flex-col items-end gap-1.5">
                <time className="text-[10px] text-slate-400">
                  {Number.isFinite(row.lastTimestamp) && row.lastTimestamp > 0
                    ? new Date(row.lastTimestamp).toLocaleDateString(
                      undefined,
                      { month: "short", day: "numeric" },
                    )
                    : ""}
                </time>
                {row.unread > 0 && !row.blocked && (
                  <b className="grid min-h-5 min-w-5 place-items-center rounded-full bg-[#327691] px-1 text-[10px] text-white">
                    {row.unread > 99 ? "99+" : row.unread}
                  </b>
                )}
              </span>
            </Link>
            {!row.isSelf && (
              <button
                aria-label={`More options for ${row.name}`}
                onClick={() => setMore(row)}
                className="grid h-9 w-7 shrink-0 place-items-center rounded-full text-slate-400"
              >
                <LuEllipsisVertical size={18} />
              </button>
            )}
          </article>
        ))}
        {!list.length && !inbox.loading && (
          <p className="py-12 text-center text-sm text-slate-500">
            {tab === "Blocked" ? "No blocked users" : "No chats found"}
          </p>
        )}
      </section>
      {tab === "Groups" && (
        <Link
          to="/create-group"
          aria-label="Create a new group"
          className="fixed bottom-28 right-6 grid h-14 w-14 place-items-center rounded-full bg-[#327691] text-white shadow-lg"
        >
          <LuPlus size={25} />
        </Link>
      )}
      <ModalSheet
        open={Boolean(more)}
        onClose={() => {
          if (!busy) setMore(null);
        }}
        title={more?.name || "Conversation"}
      >
        {error && (
          <p role="alert" className="mb-3 text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="grid gap-2">
          {[
            ["pin", LuPin, more?.pinned ? "Unpin chat" : "Pin chat"],
            ["mute", LuVolumeX, more?.muted ? "Unmute chat" : "Mute chat"],
            ...(more?.type === "dm"
              ? [
                [
                  "block",
                  LuShieldBan,
                  more?.blocked ? "Unblock user" : "Block user",
                ],
              ]
              : []),
            ["hide", LuTrash2, "Delete from my list"],
          ].map(([type, Icon, label]) => (
            <button
              key={type}
              disabled={busy}
              onClick={() => action(type)}
              className="flex items-center gap-3 rounded-xl p-3 text-left text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 dark:hover:bg-slate-800"
            >
              <Icon size={19} />
              {label}
            </button>
          ))}
        </div>
      </ModalSheet>
    </AppPage>
  );
}
